import React from "react";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash";
import { useFormContext, Controller } from "react-hook-form";
import type { Path } from "react-hook-form";
import { Fieldset } from "components/form/Fieldset";

export type IntLimitFeatureFormInput = {
  isUnlimited: boolean;
  limit?: number;
  overrun?: number;
};

interface IntLimitFeatureFormProps<TFieldValues> {
  fieldName: Path<TFieldValues>;
  limitName: string;
  limitItemName: string;
  disabled?: boolean;
}

export function IntLimitFeatureForm<TFieldValues>(props: IntLimitFeatureFormProps<TFieldValues>) {
  const { register, formState, watch, control, trigger } = useFormContext();
  const unlimitedPath = `${props.fieldName}.isUnlimited`;
  const limitPath = `${props.fieldName}.limit`;
  const limitErrors = get(formState.errors, limitPath);
  const hasLimitErrors = !!(formState.errors && limitErrors);
  const disabled = props.disabled ?? false;

  return (
    <Fieldset title={`${props.limitName}`}>
      <Grid
        container
        spacing={0}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid size={4}>
          <Controller
            name={unlimitedPath}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      trigger(limitPath);
                    }}
                    checked={field.value ?? false}
                    disabled={disabled}
                  />
                }
                label={`Unlimited?`}
                style={{ marginTop: "16px" }}
              />
            )}
          />
        </Grid>
        <Grid size={8}>
          <TextField
            fullWidth
            error={hasLimitErrors}
            helperText={String(limitErrors?.message ?? "")}
            variant="standard"
            label={`or explicit ${props.limitItemName} limit`}
            disabled={watch(unlimitedPath) === true || disabled}
            {...register(limitPath, {
              valueAsNumber: true,
              shouldUnregister: true,
              validate: {
                greaterThanZeroWhenLimited: (value?: number) => {
                  if (watch(unlimitedPath)) {
                    return true;
                  } else {
                    return (
                      (!!value && value > 0) || `The ${props.limitName.toLocaleLowerCase()} must be greater than 0.`
                    );
                  }
                },
              },
            })}
            slotProps={{
              htmlInput: { inputMode: "numeric", pattern: "[0-9]*" },
            }}
          />
        </Grid>
      </Grid>
    </Fieldset>
  );
}
